import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  getChoiceQuestions,
  getAutoNotificationTemplates,
  createSurveyEvent
} from "../../../../services/choices/choices_services";
import { useParams } from "react-router-dom";
import MainLayout from "../../../../layouts/main/main_layout";
import {
  CircularProgress,
  Box,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputLabel,
  IconButton,
  TextField,
} from "@mui/material";
import { useFeedback } from "../../../../providers/feedback/feeedback";
import { getJobCategories } from "../../../../services/jobs/jobs";
import { ArrowBack } from "@mui/icons-material";
import { MODULE_CONFIG, MODULE_TYPES } from "./module_config";
import { queryClient } from "../../../../providers/query/query";

const ChoiceQuestions = () => {

  const { id } = useParams<{ id: string }>();
  const { showSnackbar } = useFeedback();

  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const [selectedAnswer, setSelectedAnswer] = useState<any>("");
  const [selectedModuleType, setSelectedModuleType] = useState<string>("");

  const [moduleId, setModuleId] = useState<string>("");
  const [submoduleId, setSubmoduleId] = useState<string>("");

  const [selectedTemplateId, setSelectedTemplateId] = useState<any>("");


  const currentModuleConfig =
    MODULE_CONFIG[selectedModuleType] || {
      hasModule: false,
      hasSubModule: false,
    };

  const { data: choiceQuestions, isLoading } = useQuery<any>(
    ["choice-question", id],
    async () => {
      if (id) {
        const response = await getChoiceQuestions(+id);
        return response?.data?.data || [];
      }
      return [];
    },
    { enabled: !!id }
  );

  const { data: templates } = useQuery<any>(
    ["auto-notification-templates"],
    async () => {
      const response = await getAutoNotificationTemplates();
      return response?.data?.data || [];
    }
  );

  const { data: jobCategories } = useQuery<any>(
    ["job-categories"],
    async () => {
      const response = await getJobCategories();      
      return response?.data?.data || [];
    }
  );

  const { mutate: createSurveyNotificationEvent } = useMutation(createSurveyEvent, {
    onSuccess: () => {
      setSubmitLoading(false);
      showSnackbar("Survey event created successfully");
      queryClient.invalidateQueries("autoNotificationCreatedEvent");

      setSelectedQuestion(null);
      setSelectedAnswer("");
      setSelectedTemplateId("");
      setModuleId("");
      setSubmoduleId("");
      setSelectedModuleType("SCHEME");
    },
    onError: (error: any) => {
      setSubmitLoading(false);
      console.error("Error creating survey event:", error);
      showSnackbar(
        error?.response?.data?.error?.message || "Failed to create survey event"
      );
    },
  });

  const allQuestions = useMemo(() => {
    return (
      choiceQuestions?.details?.pages?.flatMap(
        (page: any) => page?.elements || []
      ).filter((q: any) => q.type === "dropdown" || q.type === "checkbox") || []
    );
  }, [choiceQuestions]);

  const handleBackClick = () => {
    if (selectedQuestion) {
      setSelectedQuestion(null);
      setSelectedAnswer("");
    } else {
      window.history.back();
    }
  }
  const handleSubmit = () => {
    const payload: any = {
      choiceId: Number(id),
      questionName: selectedQuestion?.name || "",
      questionTitle:
        typeof selectedQuestion?.title === "object"
          ? selectedQuestion?.title?.default
          : selectedQuestion?.title || "",
      answer: selectedAnswer,
      templateId: selectedTemplateId || 0,
      module: selectedModuleType,
    };

    if (moduleId) {
      payload.moduleId = moduleId
    }

    if (submoduleId) {
      payload.submoduleId = submoduleId;
    }

    setSubmitLoading(true);
    createSurveyNotificationEvent(payload);
  };

  return (
    <MainLayout>
      <Box p={2}>
        {/* HEADER */}
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <IconButton onClick={handleBackClick}>
            <ArrowBack />
          </IconButton>

          <Typography variant="h5" fontWeight={700}>
            Questions for "{choiceQuestions?.name}"
          </Typography>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* BACK BUTTON */}
            {selectedQuestion && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedQuestion(null);
                  setSelectedAnswer("");
                }}
                sx={{ mb: 2 }}
              >
                View All Questions
              </Button>
            )}

            {/* LIST VIEW */}
            {!selectedQuestion && (
              <>
                {allQuestions.length === 0 ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                    flexDirection="column"
                  >
                    <Typography variant="h6" color="text.secondary">
                      No Questions Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      No dropdown or checkbox questions available
                    </Typography>
                  </Box>
                ) : (
                  allQuestions.map((q: any, index: number) => (
                    <Box
                      key={q.id || index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        mb: 2,
                        border: "1px solid #ddd",
                        borderRadius: 2,
                      }}
                    >
                      <Typography fontWeight={600}>
                        {index + 1}. {typeof q.title === 'object' ? q?.title?.default : q.title}
                      </Typography>

                      <Button variant="contained" onClick={() => setSelectedQuestion(q)}>
                        Select Question
                      </Button>
                    </Box>
                  ))
                )}
              </>
            )}

            {/* DETAIL VIEW */}
            {selectedQuestion && (
              <Box sx={{ p: 3, border: "1px solid #ddd", borderRadius: 2 }}>
                <Typography fontWeight={600} mb={3}>
                  {typeof selectedQuestion.title === 'object' ? selectedQuestion?.title?.default : selectedQuestion.title}
                </Typography>

                {/* ANSWER */}
                {selectedQuestion.type === "dropdown" && (
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Select Answer</InputLabel>
                    <Select
                      value={selectedAnswer}
                      label="Select Answer"
                      onChange={(e) => setSelectedAnswer(e.target.value)}
                    >
                      {selectedQuestion?.choices?.map((opt: any, i: number) => (
                        <MenuItem key={i} value={typeof opt === 'object' ? opt?.value : opt}>
                          {typeof opt === 'object' ? opt?.value : opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {selectedQuestion.type === "checkbox" && (
                  <Box mb={3}>
                    {selectedQuestion?.choices?.map((opt: any, i: number) => (
                      <FormControlLabel
                        key={i}
                        control={
                          <Checkbox
                            checked={
                              selectedAnswer ===
                              (typeof opt === "object" ? opt?.value : opt)
                            }
                            onChange={() =>
                              setSelectedAnswer(
                                typeof opt === "object" ? opt?.value : opt
                              )
                            }
                          />
                        }
                        label={typeof opt === 'object' ? opt?.value : opt}
                      />
                    ))}
                  </Box>
                )}

                {/* MODULE TYPE */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Module Type</InputLabel>
                  <Select
                    value={selectedModuleType}
                    label="Module Type"
                    onChange={(e) => {
                      setSelectedModuleType(e.target.value);
                      setModuleId("");
                      setSubmoduleId("");
                    }}
                  >
                    {MODULE_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedModuleType === "JOB" ? (
                  <>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={moduleId}
                        label="Category"
                        onChange={(e) => {
                          setModuleId(e.target.value);
                          setSubmoduleId("");
                        }}
                      >
                        {jobCategories.map((item: any) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.value}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {Number(moduleId) === 39 && (
                      <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Sub Category</InputLabel>
                        <Select
                          value={submoduleId}
                          label="Sub Category"
                          onChange={(e) => setSubmoduleId(e.target.value)}
                        >
                          {jobCategories.find((item: any) => item.id === 39).subcategories?.map((item: any) => (
                            <MenuItem key={item.id} value={item.id}>
                              {item.value}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                ) : (
                  <>
                    {currentModuleConfig.hasModule && (
                      <TextField
                        fullWidth
                        sx={{ mb: 3 }}
                        label={currentModuleConfig.moduleLabel}
                        value={moduleId}
                        onChange={(e) => setModuleId(e.target.value)}
                      />
                    )}

                    {currentModuleConfig.hasSubModule && (
                      <TextField
                        fullWidth
                        sx={{ mb: 3 }}
                        label={currentModuleConfig.subModuleLabel}
                        value={submoduleId}
                        onChange={(e) => setSubmoduleId(e.target.value)}
                      />
                    )}
                  </>
                )}

                {/* TEMPLATE */}
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Template</InputLabel>
                  <Select
                    value={selectedTemplateId}
                    label="Template"
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                  >
                    {templates?.map((template: any) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* SUBMIT */}
                <Button variant="contained" onClick={handleSubmit}>
                  {submitLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </MainLayout>
  );
};

export default ChoiceQuestions;